import { FileJobStatus } from '@atlas/types'
import { Badge, Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import type { ZipRequestSummary } from '@/services/file-job.service'

interface RecentDownloadsProps {
	rows: ZipRequestSummary[]
	isLoading?: boolean
	/**
	 * How many Files the user selected for a request, keyed by requestId.
	 *
	 * Session-only enrichment and nothing more: it is gone after a refresh, and
	 * every row must still read correctly without it. Nothing about a row's
	 * existence, status or download depends on an entry being here.
	 */
	selectedCounts?: ReadonlyMap<string, number>
	onDownload: (requestId: string) => Promise<void> | void
}

/** Discrete state copy. Never a percentage, a bar, a byte count or an ETA — the backend reports none of those. */
const STATUS_LABEL: Partial<Record<FileJobStatus, string>> = {
	[FileJobStatus.QUEUED]: 'Preparing…',
	[FileJobStatus.PROCESSING]: 'Preparing…',
	[FileJobStatus.READY]: 'Ready',
	[FileJobStatus.FAILED]: 'Failed',
}

function RecentDownloadRow({
	row,
	selectedCount,
	onDownload,
}: {
	row: ZipRequestSummary
	selectedCount?: number
	onDownload: (requestId: string) => Promise<void> | void
}) {
	// Local to the row, because only the row has a control to disable. The same
	// download function is called bare from a notification action, which has none.
	const [pending, setPending] = useState(false)
	const unavailable = row.unavailableCount ?? 0

	return (
		<li className="flex items-center gap-3 py-2">
			<div className="min-w-0 flex-1">
				<p className="truncate text-ds-sm font-medium text-ds-text" title={row.fileName}>
					{row.fileName}
				</p>
				<p className="text-ds-xs text-ds-text-muted">
					{selectedCount === undefined ? null : <span>{selectedCount} files · </span>}
					<span>{STATUS_LABEL[row.status] ?? 'Preparing…'}</span>
					{row.status === FileJobStatus.READY && unavailable > 0 ? (
						<span> · {unavailable} could not be included</span>
					) : null}
				</p>
			</div>
			{row.status === FileJobStatus.READY ? (
				<Button
					variant="secondary"
					className="rounded-ds-full"
					isDisabled={pending}
					onPress={() => {
						setPending(true)
						void Promise.resolve(onDownload(row.requestId)).finally(() => setPending(false))
					}}
				>
					{pending ? 'Preparing…' : 'Download'}
				</Button>
			) : (
				// A failed request is terminal: producing another archive means a new
				// selection and a new request, so no retry is offered here or anywhere.
				<Badge variant="muted" size="sm">
					{STATUS_LABEL[row.status] ?? 'Preparing…'}
				</Badge>
			)}
		</li>
	)
}

/**
 * Recent downloads — the ZIP work currently worth showing this user, rendered
 * from the backend's own listing and nothing else.
 *
 * Purely presentational: it is handed rows and a download callback, and holds no
 * opinion about where either came from.
 */
export function RecentDownloads({ rows, isLoading, selectedCounts, onDownload }: RecentDownloadsProps) {
	if (isLoading || rows.length === 0) return null

	return (
		<section aria-labelledby="recent-downloads-heading" className="rounded-ds-lg border border-ds-border p-4">
			<h2 id="recent-downloads-heading" className="text-ds-sm font-medium text-ds-text">
				Recent downloads
			</h2>
			<ul className="mt-1 divide-y divide-ds-border">
				{rows.map((row) => (
					<RecentDownloadRow
						key={row.requestId}
						row={row}
						selectedCount={selectedCounts?.get(row.requestId)}
						onDownload={onDownload}
					/>
				))}
			</ul>
		</section>
	)
}
